/**
 * Bouwstenen van het antwoordvak: altijd dezelfde opbouw (kopregel,
 * inhoud, voet met Bijlage, Uit portaal, Notitie, menu met drie puntjes
 * en rechts de oranje hoofdknop). De vijf standen in `Standen.tsx`
 * zetten hiermee hun eigen inhoud in elkaar.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
  type TextareaHTMLAttributes,
} from "react";

import { v6Api, type TegelConcept, type V6UitgaandeBijlage, type V6Zaak } from "../api";
import { conceptBijlagenSleutel, voegBijlagenSamen } from "../bijlagen";
import { useV6, useV6Optioneel } from "../context";
import { Icoon, type IcoonNaam } from "../Icoon";
import type { ToonMelding } from "./index";
import { meetNaAanvraag } from "../meting";
import {
  naAfronden,
  naTerugzetten,
  planStilleVerversing,
  verplaatsKaart,
  verversZaak,
} from "../naActie";
import { v6Sleutels } from "../sleutels";
import {
  useVerzendingen,
  verwijderVerzending,
  verzendingenVan,
  zetVerzending,
} from "../verzendingen";
import { htmlNaarTekst } from "./mailopmaak";
import { naVijf } from "./tijd";
import { OpVastePlek } from "./voetplek";

// ── Fouten ───────────────────────────────────────────────────────────

interface FoutAntwoord {
  response?: {
    status?: number;
    data?: {
      code?: unknown;
      detail?: unknown;
      context?: { code?: unknown } | null;
    };
  };
}

/** De eigen zin van de server (sessie 48), anders een algemene zin. */
export function foutTekst(fout: unknown, standaard = "Dat lukte niet. Probeer het opnieuw."): string {
  const detail = (fout as FoutAntwoord)?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  return standaard;
}

/** Sessie 48 (B1): deze tekst is in dit gesprek al verstuurd. */
export function isAlVerstuurd(fout: unknown): boolean {
  const data = (fout as FoutAntwoord)?.response?.data;
  if (!data) return false;
  const detail = data.detail as { code?: unknown } | string | undefined;
  return (
    data.code === "al_verstuurd" ||
    data.context?.code === "al_verstuurd" ||
    (typeof detail === "object" && detail?.code === "al_verstuurd")
  );
}

export interface Dubbel {
  uitleg: string | null;
  opnieuw: () => void;
}

/**
 * Versturen met een fout die blijft staan tot de volgende poging, en de
 * dubbel-vraag bij "al_verstuurd": bevestigt JP, dan gaat dezelfde
 * aanroep opnieuw met `bevestig_dubbel`.
 *
 * Sessie 72: een tweede klik terwijl de eerste loopt doet niets (ook als
 * de knop nog niet opnieuw getekend is), en na een geslaagd antwoord wordt
 * niet meer alles onder `["v6"]` herladen: `klaar` werkt zelf alleen de
 * geraakte kaart, zaak en tellers bij (zie `../naActie.ts`).
 */
export function useVerzenden() {
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [dubbel, setDubbel] = useState<Dubbel | null>(null);
  const lopend = useRef(false);

  async function stuur<R>(
    doe: (bevestigDubbel: boolean) => Promise<R>,
    klaar: (uitkomst: R) => void,
    bevestig = false,
  ): Promise<void> {
    if (lopend.current) return;
    lopend.current = true;
    setBezig(true);
    setFout(null);
    setDubbel(null);
    try {
      const uitkomst = await doe(bevestig);
      klaar(uitkomst);
    } catch (e) {
      if (!bevestig && isAlVerstuurd(e)) {
        const detail = (e as FoutAntwoord).response?.data?.detail;
        setDubbel({
          uitleg: typeof detail === "string" ? detail : null,
          opnieuw: () => void stuur(doe, klaar, true),
        });
      } else {
        setFout(foutTekst(e, "Versturen lukte niet. Probeer het opnieuw."));
      }
    } finally {
      lopend.current = false;
      setBezig(false);
    }
  }

  return { bezig, fout, dubbel, stuur, zetFout: setFout, sluitDubbel: () => setDubbel(null) };
}

/** Fout en dubbel-vraag, boven de hoofdknop. Blijft staan tot de volgende poging. */
export function VakFout({
  fout,
  melding,
  dubbel,
  onNiet,
}: {
  fout: string | null;
  melding?: string | null;
  dubbel: Dubbel | null;
  onNiet: () => void;
}) {
  if (!fout && !melding && !dubbel) return null;
  return (
    <div className="akfout">
      {melding && <p className="v6-fout">{melding}</p>}
      {fout && (
        <p className="v6-fout" role="alert">
          {fout}
        </p>
      )}
      {dubbel && (
        <div className="dubbel" role="alertdialog" aria-label="Al verstuurd">
          <span>Deze tekst is al verstuurd. Toch nog een keer?</span>
          <button type="button" className="v6-knop" onClick={onNiet}>
            Niet
          </button>
          <button type="button" className="v6-knop acc" onClick={dubbel.opnieuw}>
            Toch versturen
          </button>
          {dubbel.uitleg && <small>{dubbel.uitleg}</small>}
        </div>
      )}
    </div>
  );
}

// ── Tekst en concept van de copiloot ─────────────────────────────────

/**
 * De tekst in het vak. Volgt het concept tot JP zelf typt; een concept
 * van de copiloot voor deze zaak vervangt de tekst (en het onderwerp als
 * dat meekomt). Het copilootvlak leest de huidige tekst via de lezer.
 */
export function useTekst(zaakId: string, basisTekst: string, basisOnderwerp: string) {
  const { conceptVoorstel, conceptOvergenomen, registreerConceptLezer } = useV6();
  const [eigen, setEigen] = useState<string | null>(null);
  const [eigenOnderwerp, setEigenOnderwerp] = useState<string | null>(null);
  const [toegepast, setToegepast] = useState(0);

  if (
    conceptVoorstel &&
    conceptVoorstel.zaakId === zaakId &&
    conceptVoorstel.volgnummer !== toegepast
  ) {
    setToegepast(conceptVoorstel.volgnummer);
    setEigen(conceptVoorstel.tekst);
    if (conceptVoorstel.onderwerp !== null) setEigenOnderwerp(conceptVoorstel.onderwerp);
  }

  // Overgenomen: meld het, zodat een nieuwe mount of standwissel het
  // voorstel niet nog eens toepast.
  useEffect(() => {
    if (toegepast) conceptOvergenomen(toegepast);
  }, [toegepast, conceptOvergenomen]);

  const tekst = eigen ?? basisTekst;
  const onderwerp = eigenOnderwerp ?? basisOnderwerp;

  // Sessie 72: mislukte het versturen, of maakte JP het ongedaan, dan komt
  // de verstuurde tekst terug, maar alleen in een leeg vak: wat JP intussen
  // typte, blijft staan.
  const qc = useQueryClient();
  const verzendingen = useVerzendingen(zaakId);
  const [teruggezet, setTeruggezet] = useState<string | null>(null);
  const terug = verzendingen.find(
    (v) =>
      !v.hersteld &&
      v.wachtrijId !== teruggezet &&
      (v.stand === "mislukt" || v.stand === "ingetrokken"),
  );
  if (terug) {
    setTeruggezet(terug.wachtrijId);
    if (!htmlNaarTekst(tekst).trim()) {
      setEigen(terug.tekst);
      if (terug.onderwerp) setEigenOnderwerp(terug.onderwerp);
    }
  }
  // Pas na de render de winkel bijwerken: deze regel is afgehandeld.
  useEffect(() => {
    if (!teruggezet) return;
    const v = verzendingenVan(qc, zaakId).find((x) => x.wachtrijId === teruggezet);
    if (!v || v.hersteld) return;
    if (v.stand === "ingetrokken") verwijderVerzending(qc, v.wachtrijId);
    else zetVerzending(qc, v.wachtrijId, { hersteld: true });
  }, [teruggezet, zaakId, qc]);

  const lezer = useRef(tekst);
  useEffect(() => {
    lezer.current = tekst;
  });
  useEffect(() => {
    // De copiloot leest platte tekst; de mailstand geeft HTML (sessie 70).
    registreerConceptLezer(() => htmlNaarTekst(lezer.current));
    return () => registreerConceptLezer(null);
  }, [registreerConceptLezer]);

  return {
    tekst,
    zetTekst: setEigen,
    onderwerp,
    zetOnderwerp: setEigenOnderwerp,
    /** Na een geslaagde verzending: het vak staat leeg, niet terug op
     *  de oude concepttekst uit de cache. */
    leeg: () => {
      setEigen("");
      setEigenOnderwerp(null);
    },
  };
}

/** Tekstveld dat meegroeit met de tekst en nooit zelf scrolt. */
export const Tekstvak = forwardRef<
  HTMLTextAreaElement,
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange"> & {
    waarde: string;
    onWaarde: (waarde: string) => void;
  }
>(function Tekstvak({ waarde, onWaarde, className, ...rest }, ref) {
  const binnen = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => binnen.current as HTMLTextAreaElement);
  useLayoutEffect(() => {
    const el = binnen.current;
    if (!el) return;
    el.style.height = "auto";
    if (el.scrollHeight > 0) el.style.height = `${el.scrollHeight}px`;
  }, [waarde]);
  return (
    <textarea
      ref={binnen}
      className={["tekst", className].filter(Boolean).join(" ")}
      value={waarde}
      onChange={(e) => onWaarde(e.target.value)}
      rows={1}
      {...rest}
    />
  );
});

// ── Conceptregel (sessie 68, punt 3) ─────────────────────────────────

/**
 * De regel bovenin een leeg vak: "geen concept", of bij een
 * afrondvoorstel "voorstel: afronden, <label>" met de knop Afronden
 * (het bestaande afrondpad). Dezelfde woorden als de kaart in de lijst;
 * het vak eronder blijft gewoon bruikbaar.
 */
export function ConceptRegel({
  zaakId,
  concept,
  tekst,
  toonMelding,
}: {
  zaakId: string;
  concept: TegelConcept;
  /** De tekst die nu in het vak staat. */
  tekst: string;
  toonMelding: ToonMelding;
}) {
  const queryClient = useQueryClient();
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const label = concept.afronden_label ?? null;
  const reden = concept.afronden_reden ?? null;
  // Staat er tekst in het vak (JP typt, of de copiloot gaf een antwoord),
  // dan antwoordt JP: geen Afronden-knop die die tekst stil weggooit.
  if (tekst.trim() || (!label && concept.tekst.trim())) return null;

  const rondAf = async () => {
    if (!reden) return;
    setBezig(true);
    setFout(null);
    try {
      const uitkomst = await v6Api.rondAf(zaakId, reden);
      const eventId = uitkomst.event_id;
      void naAfronden(queryClient, zaakId, "sluiten");
      meetNaAanvraag("sluiten", /\/afronden$/);
      toonMelding({
        tekst: "Afgerond.",
        tot: uitkomst.ongedaan_tot,
        ongedaan: eventId
          ? async () => {
              await v6Api.ongedaan(zaakId, eventId);
              naTerugzetten(queryClient, zaakId);
            }
          : null,
      });
    } catch (e) {
      setFout(foutTekst(e, "Afronden lukte niet. Probeer het opnieuw."));
    } finally {
      setBezig(false);
    }
  };

  const regel = (
    <div className="conceptregel" data-testid="v6-conceptregel">
      <span>{label ? `voorstel: afronden, ${label}` : "geen concept"}</span>
      {label && reden && (
        <button
          type="button"
          className="v6-knop acc"
          disabled={bezig}
          onClick={() => void rondAf()}
        >
          <Icoon naam="ok" klein />
          Afronden
        </button>
      )}
      {fout && (
        <span className="v6-fout" role="alert">
          {fout}
        </span>
      )}
    </div>
  );
  // Sessie 70 punt 4: een afrondvoorstel staat op de iPhone vast onderaan,
  // boven de hoofdknop; "geen concept" blijft gewoon boven het vak.
  return label && reden ? <OpVastePlek plek="regel">{regel}</OpVastePlek> : regel;
}

// ── Kopregel ─────────────────────────────────────────────────────────

export function Kopregel({
  icoon,
  titel,
  wie,
  van,
  lang,
  rechts,
}: {
  icoon: IcoonNaam;
  titel: string;
  /** Motor 7 (keuze 9): rechts in de kop, zoals Namens Sanne of JP. */
  rechts?: ReactNode;
  /** Letter in het rondje: "S" (Sanne) of "N" (Nova). */
  wie?: string | null;
  van?: string | null;
  /** Het deel van de van-regel dat op de iPhone wegvalt. */
  lang?: string | null;
}) {
  return (
    <div className="ak">
      <span className="titel">
        <span className="ic">
          <Icoon naam={icoon} klein />
        </span>
        {titel}
      </span>
      {van && (
        <span className="van">
          {wie && <span className="wie">{wie}</span>}
          <span>
            {van}
            {lang && <span className="lang">{lang}</span>}
          </span>
        </span>
      )}
      {rechts}
    </div>
  );
}

// ── Menu ─────────────────────────────────────────────────────────────

/**
 * Klein uitklapmenu; sluit bij een klik naast `houder` of Escape. De ref
 * blijft buiten het teruggegeven object, zodat de render hem niet leest.
 */
export function useMenu(houder: RefObject<HTMLDivElement>) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const klik = (e: MouseEvent) => {
      if (houder.current && !houder.current.contains(e.target as Node)) setOpen(false);
    };
    const toets = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", klik);
    document.addEventListener("keydown", toets);
    return () => {
      document.removeEventListener("mousedown", klik);
      document.removeEventListener("keydown", toets);
    };
  }, [open, houder]);
  return [open, setOpen] as const;
}

export type Moment = "vandaag_17" | "morgen_08";

function LaterKeuzes({ onKies, naVijfUur }: { onKies: (m: Moment) => void; naVijfUur: boolean }) {
  return (
    <>
      <span className="kopregel">Later versturen</span>
      <button type="button" disabled={naVijfUur} onClick={() => onKies("vandaag_17")}>
        Vandaag 17:00
      </button>
      <button type="button" onClick={() => onKies("morgen_08")}>
        Morgen 08:00
      </button>
    </>
  );
}

/** De oranje hoofdknop, altijd rechts in de voet. */
export function Hoofdknop({
  icoon,
  children,
  onClick,
  disabled,
}: {
  icoon: IcoonNaam;
  children: ReactNode;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button type="button" className="v6-knop doe" disabled={disabled} onClick={onClick}>
      <Icoon naam={icoon} klein />
      {children}
    </button>
  );
}

// ── Bijlagen ─────────────────────────────────────────────────────────

/**
 * Waar de paperclip heen gaat: `mail` = eigen bijlage als chip boven de
 * tekst (en Uit portaal), `whatsapp` = direct sturen in het gesprek,
 * `uit` = uitgeschakeld met een reden.
 */
export type BijlageModus =
  | { soort: "mail" }
  | { soort: "whatsapp"; sessieId: string }
  | { soort: "uit"; reden: string };

export interface BijlageStand {
  modus: BijlageModus;
  lijst: V6UitgaandeBijlage[];
  bezig: boolean;
  fout: string | null;
  kies: (accept?: string) => void;
  voegToe: (bijlage: V6UitgaandeBijlage) => void;
  weg: (id: string) => void;
  leeg: () => void;
  invoer: ReactNode;
}

/**
 * Sessie 55: in de mailstand komen er ook bijlagen van buiten JP bij:
 * - `conceptBijlagen`: label en pakbon uit het Sanne-concept
 *   (`concept_bijlagen`), één keer per concept. Haalt JP er een weg, of
 *   wisselt het vak van kanaal en terug, dan komen ze niet terug;
 * - het bijlagenvoorstel van de copiloot (via de context), alsof JP ze
 *   zelf via Bijlage of documenten koos.
 * Nooit dubbel op id.
 */
export function useBijlagen(
  zaakId: string,
  modus: BijlageModus,
  conceptBijlagen?: V6UitgaandeBijlage[] | null,
): BijlageStand {
  const queryClient = useQueryClient();
  const v6 = useV6Optioneel();
  const [lijst, setLijst] = useState<V6UitgaandeBijlage[]>([]);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const invoerRef = useRef<HTMLInputElement>(null);
  const mail = modus.soort === "mail";

  // Het concept van Sanne met bijlagen: één keer voorvullen.
  const sleutel = mail ? conceptBijlagenSleutel(zaakId, conceptBijlagen) : null;
  const [voorgevuld, setVoorgevuld] = useState<string | null>(null);
  if (sleutel && conceptBijlagen && sleutel !== voorgevuld) {
    setVoorgevuld(sleutel);
    if (!v6?.conceptBijlagenGezien(sleutel)) {
      setLijst((oud) => voegBijlagenSamen(oud, conceptBijlagen));
    }
  }
  const markeer = v6?.markeerConceptBijlagen;
  useEffect(() => {
    if (voorgevuld && markeer) markeer(voorgevuld);
  }, [voorgevuld, markeer]);

  // De copiloot zette bijlagen klaar voor deze zaak.
  const voorstel = v6?.bijlagenVoorstel ?? null;
  const [toegepast, setToegepast] = useState(0);
  if (mail && voorstel && voorstel.zaakId === zaakId && voorstel.volgnummer !== toegepast) {
    setToegepast(voorstel.volgnummer);
    setLijst((oud) => voegBijlagenSamen(oud, voorstel.bijlagen));
  }
  const overgenomen = v6?.bijlagenOvergenomen;
  useEffect(() => {
    if (toegepast && overgenomen) overgenomen(toegepast);
  }, [toegepast, overgenomen]);

  const opKeuze = async (bestand: File) => {
    setBezig(true);
    setFout(null);
    try {
      if (modus.soort === "mail") {
        const bijlage = await v6Api.uploadBijlage(zaakId, bestand);
        setLijst((oud) => [...oud, bijlage]);
      } else if (modus.soort === "whatsapp") {
        await v6Api.stuurWhatsappBestand(modus.sessieId, bestand);
        void queryClient.invalidateQueries({ queryKey: v6Sleutels.zaak(zaakId) });
      }
    } catch (e) {
      setFout(foutTekst(e, "Bijlage lukte niet. Probeer het opnieuw."));
    } finally {
      setBezig(false);
    }
  };

  const invoer = (
    <input
      ref={invoerRef}
      type="file"
      hidden
      data-testid="v6-bijlage-invoer"
      onChange={(e) => {
        const bestand = e.target.files?.[0];
        e.target.value = "";
        if (bestand) void opKeuze(bestand);
      }}
    />
  );

  return {
    modus,
    lijst,
    bezig,
    fout,
    kies: (a) => {
      if (modus.soort === "uit") return;
      const el = invoerRef.current;
      if (!el) return;
      // Direct in de klik: iOS opent de bestandskiezer alleen vanuit de
      // gebruikersklik zelf, niet vanuit een timer. Daarom ook accept
      // rechtstreeks op het element in plaats van via een render.
      if (a) el.setAttribute("accept", a);
      else el.removeAttribute("accept");
      el.click();
    },
    voegToe: (b) => setLijst((oud) => voegBijlagenSamen(oud, [b])),
    weg: (id) => setLijst((oud) => oud.filter((b) => b.id !== id)),
    leeg: () => setLijst([]),
    invoer,
  };
}

/** Bijlagen verschijnen pas als chip boven de tekst als er een is. */
export function BijlageChips({ bijlagen }: { bijlagen: BijlageStand }) {
  if (bijlagen.modus.soort !== "mail") return null;
  if (bijlagen.lijst.length === 0 && !bijlagen.fout) return null;
  return (
    <div className="bijlagechips">
      {bijlagen.lijst.map((b) => (
        <span key={b.id} className="chip">
          <Icoon naam="klip" klein />
          {b.naam}
          <button type="button" aria-label={`Haal ${b.naam} weg`} onClick={() => bijlagen.weg(b.id)}>
            <Icoon naam="x" klein />
          </button>
        </span>
      ))}
      {bijlagen.fout && <span className="v6-fout">{bijlagen.fout}</span>}
    </div>
  );
}

// ── Adressen ─────────────────────────────────────────────────────────

function splits(tekst: string): string[] {
  return tekst
    .split(/[\s,;]+/)
    .map((a) => a.trim())
    .filter(Boolean);
}

/** Chips per adres met een kruisje, en een veld om er een toe te voegen. */
export function AdresVeld({
  label,
  adressen,
  onAdressen,
  naam,
  rechts,
}: {
  label: string;
  adressen: string[];
  onAdressen: (adressen: string[]) => void;
  /** Naam van de klant voor de chip "Naam, adres". */
  naam?: string | null;
  rechts?: ReactNode;
}) {
  const [invoer, setInvoer] = useState("");
  // Alleen de adressen waarmee het veld begon (van de klant) krijgen de
  // naam ervoor; een adres dat JP zelf toevoegt niet.
  const [vanKlant] = useState(() => new Set(adressen));
  const neemOp = () => {
    const nieuw = splits(invoer).filter((a) => !adressen.includes(a));
    if (nieuw.length) onAdressen([...adressen, ...nieuw]);
    setInvoer("");
  };
  return (
    <div className="rij">
      <span className="l">{label}</span>
      <span className="adressen">
        {adressen.map((adres) => (
          <span key={adres} className="chip adreschip">
            <span className="adrestekst" title={adres}>
              {naam && naam !== adres && vanKlant.has(adres) ? `${naam}, ${adres}` : adres}
            </span>
            <button
              type="button"
              aria-label={`Haal ${adres} weg`}
              onClick={() => onAdressen(adressen.filter((a) => a !== adres))}
            >
              <Icoon naam="x" klein />
            </button>
          </span>
        ))}
        <input
          type="email"
          aria-label={label}
          value={invoer}
          placeholder={adressen.length ? "" : "e-mailadres"}
          onChange={(e) => setInvoer(e.target.value)}
          onBlur={neemOp}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              neemOp();
            }
          }}
        />
      </span>
      <span className="klein">{rechts}</span>
    </div>
  );
}

// ── Voet ─────────────────────────────────────────────────────────────

function Portaalmenu({
  zaakId,
  bijlagen,
  onFout,
}: {
  zaakId: string;
  bijlagen: BijlageStand;
  onFout: (fout: string) => void;
}) {
  const houder = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useMenu(houder);
  const actief = bijlagen.modus.soort === "mail";
  const documenten = useQuery({
    queryKey: [...v6Sleutels.zaak(zaakId), "documenten"],
    queryFn: () => v6Api.documenten(zaakId),
    enabled: open,
  });
  const kies = async (soort: string) => {
    setOpen(false);
    try {
      bijlagen.voegToe(await v6Api.documentAlsBijlage(zaakId, soort));
    } catch (e) {
      onFout(foutTekst(e, "Dit document lukte niet. Probeer het opnieuw."));
    }
  };
  return (
    <div className="menuhouder" ref={houder}>
      <button
        type="button"
        title={actief ? "Uit het portaal: factuur, pakbon, retourlabel" : "Documenten gaan per mail"}
        disabled={!actief}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Icoon naam="doc" klein />
        <span className="lbl">Uit portaal</span>
      </button>
      {open && (
        <div className="v6-menu" role="menu">
          {documenten.isPending && <span className="kopregel">Laden</span>}
          {documenten.isError && <span className="v6-fout">Documenten niet geladen</span>}
          {documenten.data?.map((d) => (
            <button
              key={d.soort}
              type="button"
              disabled={!d.beschikbaar}
              title={d.beschikbaar ? d.naam : d.reden_uit ?? undefined}
              onClick={() => void kies(d.soort)}
            >
              <Icoon naam="doc" klein />
              {d.naam}
              {!d.beschikbaar && d.reden_uit && <small>{d.reden_uit}</small>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Meermenu({
  zaak,
  onLater,
  onFout,
}: {
  zaak: V6Zaak;
  onLater?: (moment: Moment) => void;
  onFout: (fout: string) => void;
}) {
  const { vraagCopiloot, kies } = useV6();
  const queryClient = useQueryClient();
  const houder = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useMenu(houder);
  const [samenvoegen, setSamenvoegen] = useState(false);
  const [naVijfUur, setNaVijfUur] = useState(false);
  const kanNova =
    Boolean(zaak.live) &&
    (zaak.kanaal === "chat" || zaak.kanaal === "whatsapp") &&
    zaak.stand !== "live";

  const sluit = () => {
    setOpen(false);
    setSamenvoegen(false);
  };

  const geefAanNova = async () => {
    sluit();
    if (!zaak.live) return;
    try {
      await v6Api.teruggeven(zaak.live.sessie_id);
      verversZaak(queryClient, zaak.zaak_id);
      planStilleVerversing(queryClient, 0);
    } catch (e) {
      onFout(foutTekst(e));
    }
  };

  const voegSamen = async (inZaakId: string) => {
    sluit();
    try {
      await v6Api.samenvoegen(zaak.zaak_id, inZaakId);
      // Deze zaak gaat op in de andere: zijn kaart gaat uit beeld.
      void verplaatsKaart(queryClient, zaak.zaak_id, { map: null }, zaak.kanaal);
      verversZaak(queryClient, inZaakId);
      planStilleVerversing(queryClient, 0);
      kies(inZaakId);
    } catch (e) {
      onFout(foutTekst(e, "Samenvoegen lukte niet. Probeer het opnieuw."));
    }
  };

  return (
    <div className="menuhouder" ref={houder}>
      <button
        type="button"
        title="Meer: later versturen, aan Nova geven, vertalen, samenvoegen"
        aria-label="Meer"
        aria-expanded={open}
        onClick={() => {
          setNaVijfUur(naVijf(Date.now()));
          setSamenvoegen(false);
          setOpen(!open);
        }}
      >
        <Icoon naam="meer" klein />
      </button>
      {open && !samenvoegen && (
        <div className="v6-menu" role="menu">
          {onLater ? (
            <LaterKeuzes
              naVijfUur={naVijfUur}
              onKies={(m) => {
                sluit();
                onLater(m);
              }}
            />
          ) : (
            <button type="button" disabled>
              Later versturen
            </button>
          )}
          <button type="button" disabled={!kanNova} onClick={() => void geefAanNova()}>
            <Icoon naam="antwoord" klein />
            Aan Nova geven
          </button>
          <button
            type="button"
            onClick={() => {
              sluit();
              vraagCopiloot("Vertaal het antwoord naar de taal van de klant");
            }}
          >
            <Icoon naam="taal" klein />
            Vertalen
          </button>
          <button
            type="button"
            disabled={zaak.andere_open_zaken.length === 0}
            onClick={() => setSamenvoegen(true)}
          >
            <Icoon naam="plus" klein />
            Samenvoegen
          </button>
        </div>
      )}
      {open && samenvoegen && (
        <div className="v6-menu" role="menu">
          <span className="kopregel">Samenvoegen met</span>
          {zaak.andere_open_zaken.map((z) => (
            <button key={z.zaak_id} type="button" onClick={() => void voegSamen(z.zaak_id)}>
              <Icoon naam={z.kanaal} klein />
              {z.onderwerp}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * De voet: links Bijlage, Uit portaal, Notitie en het menu; rechts
 * altijd op dezelfde plek de hoofdknop. Notitie klapt een klein geel
 * veld open boven de voet. Op de iPhone staat de voet vast onderaan het
 * midden (sessie 70, zie `voetplek.tsx`).
 */
export function Voet({
  zaak,
  bijlagen,
  onLater,
  onFout,
  hoofdknop,
}: {
  zaak: V6Zaak;
  bijlagen: BijlageStand;
  onLater?: (moment: Moment) => void;
  onFout: (fout: string) => void;
  hoofdknop?: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [notitieOpen, setNotitieOpen] = useState(false);
  const [notitie, setNotitie] = useState("");
  const [notitieBezig, setNotitieBezig] = useState(false);
  const [notitieFout, setNotitieFout] = useState<string | null>(null);
  const bijlageUit = bijlagen.modus.soort === "uit";

  const slaOp = async () => {
    if (!notitie.trim()) return;
    setNotitieBezig(true);
    setNotitieFout(null);
    try {
      await v6Api.notitie(zaak.zaak_id, notitie.trim());
      void queryClient.invalidateQueries({ queryKey: v6Sleutels.zaak(zaak.zaak_id) });
      setNotitie("");
      setNotitieOpen(false);
    } catch (e) {
      setNotitieFout(foutTekst(e, "Notitie opslaan lukte niet. Probeer het opnieuw."));
    } finally {
      setNotitieBezig(false);
    }
  };

  return (
    <OpVastePlek plek="voet">
      {notitieOpen && (
        <div className="notitieveld">
          <textarea
            aria-label="Interne notitie"
            placeholder="Interne notitie, alleen voor het team en Nova"
            value={notitie}
            autoFocus
            onChange={(e) => setNotitie(e.target.value)}
          />
          {notitieFout && <span className="v6-fout">{notitieFout}</span>}
          <div className="knoppen">
            <button type="button" className="v6-knop" onClick={() => setNotitieOpen(false)}>
              Annuleer
            </button>
            <button
              type="button"
              className="v6-knop zacht"
              disabled={notitieBezig || !notitie.trim()}
              onClick={() => void slaOp()}
            >
              Opslaan
            </button>
          </div>
        </div>
      )}
      {bijlagen.modus.soort === "whatsapp" && bijlagen.fout && (
        <div className="akfout">
          <p className="v6-fout">{bijlagen.fout}</p>
        </div>
      )}
      <div className="voet">
        <div className="rest">
          <button
            type="button"
            title={bijlagen.modus.soort === "uit" ? bijlagen.modus.reden : "Bijlage toevoegen"}
            disabled={bijlageUit || bijlagen.bezig}
            onClick={() => bijlagen.kies()}
          >
            <Icoon naam="klip" klein />
            <span className="lbl">Bijlage</span>
          </button>
          <Portaalmenu zaakId={zaak.zaak_id} bijlagen={bijlagen} onFout={onFout} />
          <button
            type="button"
            title="Interne notitie"
            aria-expanded={notitieOpen}
            onClick={() => setNotitieOpen(!notitieOpen)}
          >
            <Icoon naam="notitie" klein />
            <span className="lbl">Notitie</span>
          </button>
          <Meermenu zaak={zaak} onLater={onLater} onFout={onFout} />
        </div>
        {hoofdknop}
        {bijlagen.invoer}
      </div>
    </OpVastePlek>
  );
}
